import { Clock3, Eye, FileDiff, FolderGit2, UserRound } from "lucide-react";
import React, { useState } from "react";
import { formatProjectName } from "../utils";
import { ActivityBadge } from "./ActivityBadge";
import { ApprovalStatus } from "./ApprovalStatus";
import { CopyCheckoutButton } from "./CopyCheckoutButton";
import { CopyLinkButton } from "./CopyLinkButton";
import { DateTime } from "./DateTime";
import { HighlightedText } from "./HighlightedText";
import {
	MRCommentsIconButton,
	MRCommentsPanel,
	useMRComments,
} from "./MRComments";
import { MRStatusDot } from "./MRStatusDot";
import { BotIcon, DraftIcon, MRStatusIcons } from "./MRStatusIcons";
import { MRTagsEditor } from "./MRTags";
import { OpenInGitLabButton } from "./OpenInGitLabButton";
import { PipelineStatus } from "./PipelineStatus";
import { Tooltip, TooltipBadgeContent } from "./Tooltip";
import { TruncatedTitleLink } from "./TruncatedTitleLink";

export const MergeRequest = ({
	mr,
	greyOutBlockingComments,
	isMyOpenMR,
	enableCommentsExpansion = false,
	onContextMenu,
	onSelect,
	isSelected = false,
	isWatched = false,
	activity = null,
	onActivitySeen,
	onOpenDiff,
	className = "",
	searchQuery = "",
	tags = [],
	allTags = [],
	onAddTag,
	onRemoveTag,
	statePill = null,
}) => {
	const comments = useMRComments(mr);
	const [showDiffHoverHint, setShowDiffHoverHint] = useState(false);

	if (!mr) return null;

	const highlightMyOpenMR = isMyOpenMR && mr.discussions > 0;

	const stateStyles = {
		opened: "",
		merged: "border-l-4 border-l-[var(--color-row-green)]",
		closed: "border-l-4 border-l-red-600 dark:border-l-red-500",
	};

	const author = mr.author || {};
	const isClosed = mr.state === "merged" || mr.state === "closed";
	const currentUserApproved =
		mr.state === "opened" &&
		["REVIEWED", "REQUESTED_CHANGES", "APPROVED"].includes(mr.myReview);
	const silent =
		currentUserApproved ||
		(greyOutBlockingComments &&
			mr.discussions > 0 &&
			mr.hasHumanReview &&
			!isMyOpenMR);

	const iconClass = "w-4 h-4";
	const projectName = formatProjectName(mr.projectPath);
	const shouldSkipSelection = (target) =>
		target.closest?.(
			'a, button, input, textarea, select, label, [role="button"], [role="menuitem"], [data-no-row-select]',
		);

	return (
		<div
			onClick={
				onSelect
					? (event) => {
							if (shouldSkipSelection(event.target)) return;
							onSelect(event, mr);
						}
					: undefined
			}
			onKeyDown={
				onSelect
					? (event) => {
							if (event.key !== "Enter" && event.key !== " ") return;
							if (shouldSkipSelection(event.target)) return;
							event.preventDefault();
							onSelect(event, mr);
						}
					: undefined
			}
			tabIndex={onSelect ? 0 : undefined}
			onContextMenu={
				onContextMenu ? (event) => onContextMenu(event, mr) : undefined
			}
			onMouseMove={
				onOpenDiff
					? (event) => {
							setShowDiffHoverHint(!shouldSkipSelection(event.target));
						}
					: undefined
			}
			onMouseLeave={onOpenDiff ? () => setShowDiffHoverHint(false) : undefined}
			className={`
        relative
        group
        interactive-surface
        animate-in fade-in slide-in-from-bottom-2 duration-300 ease-out
        ${onSelect ? "select-none cursor-pointer" : ""}
        bg-[var(--color-bg-primary)] rounded-md shadow p-3 mb-3
        ${silent ? "opacity-60 hover:opacity-100 transition-opacity duration-200" : ""}
        border border-[var(--color-border)]
        ${currentUserApproved ? "border-l-4 border-l-[var(--color-row-green)]" : ""}
        transition-colors duration-200
        hover:bg-[var(--color-row-hover)]
        hover:border-[var(--color-border-hover)]
        ${activity?.length ? "ring-1 ring-[var(--color-accent-ring)]" : ""}
        ${stateStyles[mr.state] || ""}
        ${highlightMyOpenMR ? "border-l-4 border-l-yellow-400 dark:border-l-yellow-500" : ""}
        ${isSelected ? "ring-1 ring-[var(--color-selection-ring)] border-[var(--color-selection)] hover:border-[var(--color-selection)]" : ""}
        ${className}
      `}
		>
			{onOpenDiff && (
				<div
					className={`pointer-events-none absolute left-1/2 top-0 z-20 hidden -translate-x-1/2 -translate-y-[calc(100%+0.5rem)] transition-opacity sm:block ${
						showDiffHoverHint ? "opacity-100" : "opacity-0"
					}`}
				>
					<div
						role="tooltip"
						aria-hidden={!showDiffHoverHint}
						className={`inline-flex w-max max-w-[min(22rem,calc(100vw-2rem))] items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-2.5 py-1.5 text-xs text-[var(--color-text-primary)] shadow-lg ${
							showDiffHoverHint ? "tooltip-pop-in" : ""
						}`}
					>
						<FileDiff className="h-3.5 w-3.5 text-[var(--color-text-tertiary)]" />
						Click to open diff
					</div>
				</div>
			)}
			<div className="flex items-start gap-2">
				<MRStatusDot
					mr={mr}
					wrapperClassName="inline-flex h-6 shrink-0 items-center self-start"
				/>

				<div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-start">
					<div className="min-w-0 sm:flex-1">
						<div className="flex flex-wrap items-center gap-2">
							<TruncatedTitleLink
								href={mr.webUrl}
								title={mr.title}
								prefix={`#${mr.iid}: `}
								query={searchQuery}
								onClick={() => onActivitySeen?.(mr)}
								wrapperClassName="flex-1"
								className="text-base font-semibold text-[var(--color-link)] hover:underline"
							/>
							{activity?.length > 0 && (
								<ActivityBadge
									activity={activity}
									onDismiss={
										onActivitySeen ? () => onActivitySeen(mr) : undefined
									}
								/>
							)}
							{statePill}
						</div>

						<div className="mt-1 flex min-w-0 items-center gap-3 overflow-hidden whitespace-nowrap text-sm text-[var(--color-text-secondary)]">
							<a
								href={mr.webUrl?.split("/-/")[0]}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex min-w-0 shrink items-center gap-1.5 hover:underline"
							>
								<FolderGit2 className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-tertiary)]" />
								<span className="truncate">
									<HighlightedText query={searchQuery}>
										{projectName}
									</HighlightedText>
								</span>
							</a>
							<span className="inline-flex shrink-0 items-center gap-1.5">
								<Clock3 className="h-3.5 w-3.5 text-[var(--color-text-tertiary)]" />
								<DateTime date={mr.createdAt} tooltipPrefix="Created" />
								<span className="text-[var(--color-text-tertiary)]">/</span>
								<DateTime date={mr.updatedAt} tooltipPrefix="Updated" />
							</span>
							<span className="ml-auto inline-flex min-w-0 shrink items-center justify-end gap-1.5 text-right">
								<UserRound className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-tertiary)]" />
								<span className="min-w-0 truncate">
									<HighlightedText query={searchQuery}>
										{author.name}
									</HighlightedText>
								</span>
								<BotIcon author={author} iconClass="h-3.5 w-3.5 shrink-0" />
							</span>
						</div>
					</div>

					<div className="flex flex-col items-end gap-2 sm:w-[20rem] sm:shrink-0">
						<MRTagsEditor
							mr={mr}
							tags={tags}
							allTags={allTags}
							onAddTag={onAddTag}
							onRemoveTag={onRemoveTag}
							className="max-w-full justify-end sm:min-h-7"
						/>
						<div className="flex flex-wrap items-center justify-end gap-3">
							{isWatched ? (
								<Tooltip
									content={
										<TooltipBadgeContent badge="Watch">
											Watching this MR.
										</TooltipBadgeContent>
									}
								>
									<Eye className="w-4 h-4 text-[var(--color-accent)]" />
								</Tooltip>
							) : (
								<span
									className="hidden w-4 shrink-0 sm:inline-flex"
									aria-hidden="true"
								/>
							)}
							<MRCommentsIconButton
								comments={comments}
								count={mr.userNotesCount}
							/>
							<CopyLinkButton mr={mr} />
							<OpenInGitLabButton mr={mr} />
							<CopyCheckoutButton mr={mr} />
							<MRStatusIcons mr={mr} iconClass={iconClass} />

							{!isClosed && (
								<>
									<PipelineStatus pipeline={mr.headPipeline} />
									<ApprovalStatus
										aiReviewMissing={mr.aiReviewMissing}
										approvers={mr.approvers}
										reviewers={mr.reviewers}
										approvalsLeft={mr.approvalsLeft}
									/>
								</>
							)}

							<DraftIcon draft={mr.draft} iconClass={iconClass} />
						</div>
					</div>
				</div>
			</div>

			{(enableCommentsExpansion ||
				comments.modalOpen ||
				comments.modalEntry) && (
				<MRCommentsPanel
					comments={comments}
					className="mt-2 pt-2 border-t border-[var(--color-border)]"
				/>
			)}
		</div>
	);
};

export default MergeRequest;
